/**
 * docs-toc.js -- scrollspy for the post-build-injected on-page TOC.
 *
 * The <aside class="docs-toc"> aside is server-rendered by the
 * injectPageToc hook in cmd/brio/toc.go; this module only wires
 * the active-link state. Each TOC link's href is "#section-id";
 * for every matching <h2> in the page we observe its viewport
 * intersection and toggle aria-current="true" on the link whose
 * heading is currently in (or just above) the reader's view.
 *
 * Web platform mechanics:
 *
 * - IntersectionObserver fires when an observed element's
 *   intersection ratio crosses any of the configured thresholds.
 *   Cheaper than a scroll listener; the browser batches callbacks
 *   off the main thread.
 *
 * - rootMargin shrinks the effective viewport for intersection
 *   tests. "-20% 0px -70% 0px" means: an H2 only counts as
 *   "intersecting" while it sits in the band 20% to 30% from
 *   the top of the viewport (the top 20% and bottom 70% are
 *   excluded). That biases activation toward whichever section
 *   the reader is just past the start of -- the conventional
 *   scrollspy feel.
 *
 * - aria-current="true" rather than .is-active per PRINCIPLES.md
 *   P5: interactive state binds to the announced ARIA attribute,
 *   not a class. CSS targets [aria-current="true"] in src/docs/docs.css.
 */

const toc = document.querySelector('.docs-toc');

if (toc) {
    const links = new Map();
    for (const a of toc.querySelectorAll('a[href^="#"]')) {
        const id = a.getAttribute('href').slice(1);
        if (id) links.set(id, a);
    }

    const headings = [];
    for (const id of links.keys()) {
        const h = document.getElementById(id);
        if (h) headings.push(h);
    }

    const setCurrent = (id) => {
        for (const a of links.values()) {
            a.removeAttribute('aria-current');
        }
        const active = links.get(id);
        if (active) active.setAttribute('aria-current', 'true');
    };

    const io = new IntersectionObserver(
        (entries) => {
            const visible = entries.filter((e) => e.isIntersecting);
            if (visible.length === 0) return;
            // Pick the topmost heading currently in the activation band;
            // if multiple sections are in view, the one nearest the band's
            // top edge is the best "current" choice.
            visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
            setCurrent(visible[0].target.id);
        },
        {
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0,
        }
    );

    for (const h of headings) io.observe(h);

    // Anchor-jump support. Clicking a TOC link (or landing on the page
    // with a hash) scrolls the heading to viewport top, which lands it
    // above the IO's activation band -- the observer never fires, so
    // aria-current would otherwise stay on whatever was previously
    // current. Sync explicitly on click and on hashchange; the IO
    // resumes managing state once the user scrolls.
    const syncFromHash = () => {
        const id = location.hash.slice(1);
        if (id && links.has(id)) setCurrent(id);
    };

    toc.addEventListener('click', (e) => {
        const a = e.target.closest('a[href^="#"]');
        if (!a) return;
        const id = a.getAttribute('href').slice(1);
        if (links.has(id)) setCurrent(id);
    });

    window.addEventListener('hashchange', syncFromHash);
    syncFromHash();

    // Cross-breakpoint resync. If the user closes the TOC on mobile
    // and then resizes to desktop (>= 80rem), CSS hides the summary
    // -- there'd be no way to reopen the list. Reassert [open]
    // whenever the desktop breakpoint matches so the desktop view
    // always shows the list regardless of mobile collapse state.
    const details = toc.querySelector('.details');
    if (details) {
        const desktop = window.matchMedia('(min-width: 80rem)');
        const syncOpen = () => {
            if (desktop.matches) details.setAttribute('open', '');
        };
        desktop.addEventListener('change', syncOpen);
        syncOpen();
    }
}
